import React, { useCallback, useEffect, useState } from 'react';
import { isConditionalOption } from 'src/helpers/question-helper';
import { QueryFactory } from 'src/services/api';
import { ISurveyOption, ISurveyQuestion, QuestionType, SkipOptions, SurveyOption } from 'src/services/api/api-client';
import { changeField } from '../components/editableField/editableField.component';
import { createCommonOption } from '../helpers/option-helpers';
import { normalizeQuestionByType } from '../helpers/questionType-helpers';
import {
  getQuestionById,
  moveQuestion,
  pasteQuestionAfter,
  reorderRootQuestions,
  removeQuestionById,
  toCreateQuestionDto,
} from '../helpers/questionScheme-helper';
import { Editor } from '../sections/editor/editor.component';
import { QuestionToolBox as QuestionToolBox } from '../sections/questionToolBox/questionToolBox.component';
import { TopToolBox } from '../sections/topToolBox/topToolBox.component';
import { QuestionPreview } from '../components/questionPreview/questionPreview.component';
import { ErrorKeys, ValidationRules } from '../validation/ValidationRules';
import { Route } from './questionSchemeUIEditor';
import {
  defaultEditableQuestionValues,
  defaultQuestionSchemeEditorValues,
  EditableQuestionContext,
  QuestionSchemeEditorContext,
} from './questionSchemeUIEditor.context';
import Style from './questionSchemeUIEditor.module.css';

export const QuestionSchemeEditorContainer = () => {
  const isContentQuestion = (questionType: QuestionType) =>
    questionType === QuestionType.Content || questionType === QuestionType.Gallery;

  // QuestionSchemeEditorContext
  const [questionScheme, setQuestionScheme] = useState(defaultQuestionSchemeEditorValues.questionScheme);
  const [selectedQuestion, selectQuestion] = useState(defaultQuestionSchemeEditorValues.selectedQuestion);
  const [errors, setErrors] = useState(defaultQuestionSchemeEditorValues.errors);
  const [serverError, setServerError] = useState<string | null>(null);

  // EditableQuestionContext
  const [editableOptionId, setEditableOptionId] = useState(defaultEditableQuestionValues.editableOptionId);
  const [editableQuestion, setEditableQuestion] = useState<ISurveyQuestion | null>(null);
  const [previewQuestionId, setPreviewQuestionId] = useState<string | null>(null);

  const resetErrorHandler = useCallback((key: ErrorKeys) => {
    setErrors((x) => {
      delete x[key];
      return { ...x };
    });
  }, []);

  const setErrorHandler = useCallback((key: ErrorKeys, text: string) => {
    setErrors((x) => {
      return { ...x, [key]: text };
    });
  }, []);

  // Validation
  useEffect(() => {
    if (!editableQuestion) {
      return;
    }

    const validationErrors: { [key: string]: string } = {};
    Object.values(ValidationRules).forEach((validationRule) => {
      if (validationRule.validate(editableQuestion)) {
        validationErrors[validationRule.key] ??= validationRule.text;
      }
    });
    setErrors(validationErrors);
  }, [editableQuestion]);

  useEffect(() => {
    if (editableQuestion) {
      setServerError(null);
    }
  }, [editableQuestion]);

  // This effect reset toolbars when scheme changed
  useEffect(() => {
    selectQuestion(null);
    setEditableOptionId(null);
    setPreviewQuestionId(null);
  }, [questionScheme]);

  // This effect reset option toolbar when question changed
  useEffect(() => {
    setEditableOptionId(null);
  }, [selectedQuestion]);

  useEffect(() => {
    if (selectedQuestion?.id) {
      setPreviewQuestionId(selectedQuestion.id);
    }
  }, [selectedQuestion]);

  const changeQuestionFieldHandler = useCallback(
    (key: string, newValue: string, route?: Route) => {
      const questionSchemeCopy = Object.assign({}, questionScheme);

      if (!route || !route.questionId || route.optionIndex === undefined) {
        Object.assign(questionSchemeCopy, { [key]: newValue });
      } else {
        const foundQuestion = getQuestionById(questionSchemeCopy, route.questionId);

        if (!foundQuestion?.options) {
          return;
        }

        const option = foundQuestion?.options[route.optionIndex];
        if (isConditionalOption(option) && option.questionScheme) {
          Object.assign(option.questionScheme, { [key]: newValue });
        }
      }

      setQuestionScheme(questionSchemeCopy);
    },
    [questionScheme],
  );

  const saveQuestionHandler = useCallback(
    async (questionId: string, newQuestion: ISurveyQuestion | null) => {
      if (!newQuestion || !questionScheme) {
        return false;
      }

      if (!isContentQuestion(newQuestion.questionType) && !newQuestion.options?.length) {
        return false;
      }

      try {
        const questionFromServer = await QueryFactory.QuestionSchemeQuery.Client.createQuestion(
          toCreateQuestionDto(newQuestion),
        );

        const foundQuestion = getQuestionById(questionScheme, questionId);
        if (!foundQuestion || !questionFromServer) {
          return false;
        }

        Object.assign(foundQuestion, questionFromServer);
        const changedQuestionScheme = Object.assign({}, questionScheme);
        setQuestionScheme(changedQuestionScheme);
      } catch (error) {
        const errorText = "Server error: can't create question. Question scheme is not valid";
        setServerError(errorText);
        alert(errorText);
        return false;
      }

      return true;
    },
    [questionScheme],
  );

  const createQuestionHandler = useCallback(
    async (route?: Route) => {
      if (!questionScheme) {
        return false;
      }

      const newQuestion: ISurveyQuestion = {
        id: '',
        discriminator: 'SingleChoseQuestion',
        questionType: QuestionType.SingleChose,
        isYesNoQuestion: false,
        questionText: '',
        hashCode: 0,
        isSkippedRender: SkipOptions.NotSkip,
        canBeSkipped: false,
        contentBlock: null,
        options: [
          createCommonOption({
            optionText: 'Option0',
            optionValue: 'Value0',
            scoreValue: 0,
          }),
        ],
      };

      try {
        const questionFromServer = await QueryFactory.QuestionSchemeQuery.Client.createQuestion(
          toCreateQuestionDto(newQuestion),
        );

        if (!questionFromServer) {
          return false;
        }

        let changedQuestionScheme = null;

        // Add new question in other scheme
        if (route?.questionId && route.optionIndex !== undefined) {
          const foundQuestion = getQuestionById(questionScheme, route.questionId);

          if (!foundQuestion || !foundQuestion?.options) {
            return false;
          }

          const foundOption = foundQuestion?.options[route.optionIndex];
          if (isConditionalOption(foundOption) && foundOption.questionScheme) {
            foundOption.questionScheme.questions = [
              ...(foundOption.questionScheme?.questions ?? []),
              questionFromServer,
            ];

            changedQuestionScheme = Object.assign({}, questionScheme);
          } else {
            return false;
          }
        } else {
          // Add new question in root
          const newQuestionScheme = {
            ...questionScheme,
            questions: [...(questionScheme?.questions ?? []), questionFromServer],
          };
          changedQuestionScheme = Object.assign({}, questionScheme, newQuestionScheme);
        }

        setQuestionScheme(changedQuestionScheme);
      } catch (error) {
        const errorText = "Server error: can't create question. Question scheme is not valid";
        setServerError(errorText);
        alert(errorText);
        return false;
      }

      return true;
    },
    [questionScheme],
  );

  const copySelectedQuestionHandler = useCallback(
    async (questionId: string) => {
      if (!questionScheme) {
        return false;
      }

      const newQuestion = getQuestionById(questionScheme, questionId);
      if (!newQuestion) {
        return false;
      }

      try {
        const questionFromServer = await QueryFactory.QuestionSchemeQuery.Client.createQuestion(
          toCreateQuestionDto(newQuestion),
        );

        if (!questionFromServer) {
          return false;
        }

        const changedQuestionScheme = pasteQuestionAfter(questionScheme, questionId, questionFromServer);
        setQuestionScheme(changedQuestionScheme);
      } catch (error) {
        const errorText = "Server error: can't create question";
        setServerError(errorText);
        alert(errorText);
      }

      return true;
    },
    [questionScheme],
  );

  const deleteQuestionHandler = useCallback(
    async (questionId: string) => {
      if (!questionId || !questionScheme) {
        return false;
      }

      const changedQuestionScheme = removeQuestionById(questionScheme, questionId);
      setQuestionScheme(changedQuestionScheme);
      return true;
    },
    [questionScheme],
  );

  const moveQuestionHandler = useCallback(
    async (questionId: string, direction: 'Down' | 'Up') => {
      if (!questionId || !questionScheme?.questions) {
        return false;
      }

      const changedQuestionScheme = moveQuestion(questionScheme, questionId, direction);
      setQuestionScheme(changedQuestionScheme);
      return true;
    },
    [questionScheme],
  );

  const reorderRootQuestionsHandler = useCallback(
    (sourceId: string, targetId: string) => {
      if (!questionScheme?.questions?.length) {
        return;
      }

      const changedQuestionScheme = reorderRootQuestions(questionScheme, sourceId, targetId);
      setQuestionScheme(changedQuestionScheme);
    },
    [questionScheme],
  );

  const deleteOptionHandler = useCallback(
    (optionId: number) => {
      if (!editableQuestion || !editableQuestion.options) {
        return;
      }
      const newOptions = editableQuestion.options?.filter((option) => option !== editableQuestion.options![optionId]);
      setEditableQuestion(changeField<ISurveyQuestion>(editableQuestion, 'options', newOptions));

      setEditableOptionId(null);
    },
    [editableQuestion],
  );

  const editOptionHandler = useCallback(
    (optionId: number, newOption: ISurveyOption) => {
      if (!editableQuestion || !editableQuestion.options) {
        return;
      }

      const newOptions = Array.from(editableQuestion.options, (option, index) => {
        return index === optionId ? (newOption as SurveyOption) : option;
      });

      setEditableQuestion(changeField<ISurveyQuestion>(editableQuestion, 'options', newOptions));
    },
    [editableQuestion],
  );

  const setEditableQuestionHandler = useCallback((question: ISurveyQuestion | null) => {
    setEditableQuestion(question ? normalizeQuestionByType(question, question.questionType) : null);
  }, []);

  const updateEditableQuestionHandler = useCallback((question: ISurveyQuestion | null) => {
    setEditableQuestion(question);
    if (question) {
      selectQuestion(question);
    }
  }, []);

  const previewQuestion =
    editableQuestion && previewQuestionId === editableQuestion.id
      ? editableQuestion
      : previewQuestionId && questionScheme
      ? getQuestionById(questionScheme, previewQuestionId)
      : editableQuestion ?? selectedQuestion;
  const rootQuestions = questionScheme?.questions ?? [];
  const previewQuestionIndex = previewQuestion
    ? rootQuestions.findIndex((question) => question.id === previewQuestion.id)
    : -1;

  const selectPreviewQuestionHandler = useCallback(
    (questionId: string) => {
      setPreviewQuestionId(questionId);
      const foundQuestion = questionScheme ? getQuestionById(questionScheme, questionId) : null;
      if (foundQuestion) {
        selectQuestion(foundQuestion);
        setEditableQuestionHandler(foundQuestion);
      }
    },
    [questionScheme, setEditableQuestionHandler],
  );

  const previewStepHandler = useCallback(
    (direction: 'next' | 'prev') => {
      if (!rootQuestions.length) {
        return;
      }

      const currentIndex = previewQuestionIndex >= 0 ? previewQuestionIndex : 0;
      const nextIndex =
        direction === 'next' ? Math.min(rootQuestions.length - 1, currentIndex + 1) : Math.max(0, currentIndex - 1);

      const nextQuestion = rootQuestions[nextIndex];
      if (nextQuestion) {
        selectPreviewQuestionHandler(nextQuestion.id);
      }
    },
    [previewQuestionIndex, rootQuestions, selectPreviewQuestionHandler],
  );

  const setPreviewDefaultValueHandler = useCallback(
    (optionValue: string | null) => {
      if (!editableQuestion?.options) {
        return;
      }

      const newOptions = editableQuestion.options.map((option) =>
        SurveyOption.fromJS({
          ...option.toJSON(),
          defaultValue: optionValue ? (option.optionValue === optionValue ? optionValue : undefined) : undefined,
        }),
      );

      setEditableQuestion(changeField<ISurveyQuestion>(editableQuestion, 'options', newOptions));
    },
    [editableQuestion],
  );

  return (
    <QuestionSchemeEditorContext.Provider
      value={{
        questionScheme: questionScheme,
        setQuestionScheme: setQuestionScheme,

        selectedQuestion: selectedQuestion,
        selectQuestion: selectQuestion,

        errors: errors,
        serverError: serverError,
        setError: setErrorHandler,
        resetError: resetErrorHandler,
        setServerError: setServerError,

        saveQuestion: saveQuestionHandler,
        createQuestion: createQuestionHandler,
        deleteQuestion: deleteQuestionHandler,
        copySelectedQuestion: copySelectedQuestionHandler,
        moveQuestion: moveQuestionHandler,
        changeQuestionField: changeQuestionFieldHandler,
        reorderRootQuestions: reorderRootQuestionsHandler,
      }}
    >
      <div className={Style.container}>
        <div className={Style.topToolBox}>
          <TopToolBox />
        </div>
        <EditableQuestionContext.Provider
          value={{
            editableQuestion: editableQuestion,
            setEditableQuestion: updateEditableQuestionHandler,

            editableOptionId: editableOptionId,
            setEditableOptionId: setEditableOptionId,
            deleteOption: deleteOptionHandler,
            editOption: editOptionHandler,
          }}
        >
          <div className={Style.builderLayout}>
            <div className={Style.editor}>
              <Editor scheme={questionScheme} />
            </div>
            <div className={Style.previewPanel}>
              <div className={Style.previewHeader}>{'Live Preview'}</div>
              <div className={Style.previewCanvas}>
                <QuestionPreview
                  question={previewQuestion}
                  index={previewQuestionIndex >= 0 ? previewQuestionIndex + 1 : undefined}
                  total={rootQuestions.length}
                  onPrev={() => previewStepHandler('prev')}
                  onNext={() => previewStepHandler('next')}
                  onSelectQuestion={selectPreviewQuestionHandler}
                  onDefaultValueChange={setPreviewDefaultValueHandler}
                />
              </div>
            </div>
            <div className={Style.propertiesPanel}>
              <div className={Style.questionToolBox}>
                <QuestionToolBox />
              </div>
            </div>
          </div>
        </EditableQuestionContext.Provider>
      </div>
    </QuestionSchemeEditorContext.Provider>
  );
};
